import type { InfiniteData } from '@tanstack/react-query';

import type { GetConversationsParams } from 'clients/ConversationsClient/ConversationsClient.types';
import type { PaginatedResponse } from 'clients/types';
import type { Conversation } from 'components/ui/organisms/CentralInbox/CentralInbox.types';

import { useInfiniteQuery } from '@tanstack/react-query';
import { campaignsClient } from 'clients/CampaignsClient/CampaignsClient';
import { conversationsClient } from 'clients/ConversationsClient/ConversationsClient';
import { CONVERSATION_LIMIT } from 'config';
import { queryClient } from 'config/queryClient';
import { QueryKey } from 'types/query';

export const useInfiniteConversationsQuery = ({
  limit = CONVERSATION_LIMIT,
  conversationScope,
  teamView,
}: GetConversationsParams) => {
  return useInfiniteQuery<PaginatedResponse<Conversation>>(
    [QueryKey.getInfiniteConversations, { limit, conversationScope, teamView }],
    async ({ pageParam }) => {
      const offset = pageParam?.offset ?? 0;

      const conversationsResponse = await conversationsClient.getConversations({
        limit,
        offset,
        conversationScope,
        teamView,
      });
      const esPersonIds = conversationsResponse.data.results.map((conversation) => conversation.es_person_id);

      const candidatesResponse = await campaignsClient.searchEsCampaignCandidate({ esPersonIds });

      return {
        ...conversationsResponse.data,
        results: conversationsResponse.data.results.flatMap((result) => {
          const candidate = candidatesResponse.data.results.find(
            (candidate) => candidate.es_person_id == result.es_person_id,
          );

          if (!candidate) return [];

          return [
            {
              id: result.id,
              status: result.status,
              campaign: {
                name: result.campaign.name,
              },
              source: result.source,
              user: {
                id: result.user.id,
                firstName: result.user.first_name,
                lastName: result.user.last_name,
              },
              unreadCount: result.unread_count,
              lastMessageDate: result.last_message_date,
              lastMessageFromRecruiter: result.last_message_from_recruiter,
              candidateName: result.candidate_name,
              esPersonId: result.es_person_id,
              conversationSource: result.conversation_source,
              candidate: candidate,
            },
          ];
        }) as Conversation[],
      };
    },
    {
      getNextPageParam: (_, allPages) => {
        const lastPage = allPages.at(allPages.length - 1);
        if (lastPage?.next === null) return undefined;

        const total = allPages.length;
        return { offset: total * CONVERSATION_LIMIT };
      },
    },
  );
};

export const updateCentralInboxSidebar = ({ esPersonId }: { esPersonId: string; pageSize?: number }) => {
  const [query] = queryClient
    .getQueryCache()
    .findAll()
    .filter((q) => Array.isArray(q.queryKey) && q.queryKey[0] === QueryKey.getInfiniteConversations)
    .sort((a, b) => (b.state.dataUpdatedAt ?? 0) - (a.state.dataUpdatedAt ?? 0));

  if (query) {
    queryClient.setQueryData<InfiniteData<PaginatedResponse<Conversation>>>(query.queryKey, (data) => {
      if (!data) return data;

      const allPages = data.pages.map((page) => ({ ...page, results: [...page.results] }));
      const allResults = allPages.flatMap((page) => page.results);
      const targetConversationIndex = allResults.findIndex((c) => c.esPersonId === esPersonId);

      if (targetConversationIndex === -1) return data;

      const [targetConversation] = allResults.splice(targetConversationIndex, 1);
      targetConversation.lastMessageDate = null;
      allResults.unshift(targetConversation);

      const pages = allPages.reduce((pages, page, pageIndex) => {
        const results = allResults.slice(CONVERSATION_LIMIT * pageIndex, CONVERSATION_LIMIT * (pageIndex + 1));

        return [...pages, { ...page, results }];
      }, [] as PaginatedResponse<Conversation>[]);

      return { ...data, pages };
    });
  }

  setTimeout(() => {
    queryClient.invalidateQueries([QueryKey.getInfiniteConversations], { exact: false });
  }, 5000);
};

export const getConversationList = (data: InfiniteData<PaginatedResponse<Conversation>> | undefined) =>
  data?.pages.flatMap((page) => page.results) ?? [];
